import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Save, Printer, CheckCircle2, AlertCircle, User, Calendar, Phone, MapPin, Heart, Pill, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { fillEmpty, mergeSaved } from '../../utils/formMerge';
import SignatureCanvas from 'react-signature-canvas';
import { exportFormPdfFromDom } from '../../utils/domFormPdf';
import { AGENCY } from '../../utils/agencyInfo';

const EmergencyInfoForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const patientId = searchParams.get('patientId');
  const signatureRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Client Information
    clientName: '',
    address: '',
    dateOfBirth: '',
    phoneNumber: '',
    sex: '',
    religion: '',
    birthPlace: '',
    hairColor: '',
    eyeColor: '',
    distinguishingFeatures: '',
    
    // Doctor Information
    doctorName: '',
    doctorPhone: '',
    
    // Medical Information
    specialMedicalConditions: '',
    allergies: '',
    
    // Emergency Contact
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelationship: '',
    
    // Additional Info
    additionalInfo: '',
    pets: '',
    
    // Signature
    clientSignature: '',
    signatureDate: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    if (patientId) {
      fetchPatientData();
      fetchExistingForm();
    }
  }, [patientId]);

  const fetchExistingForm = async () => {
    try {
      const response = await api.get(`/admission-forms/${patientId}/emergency_info`);
      if (response.data && response.data.form && response.data.form.data) {
        const savedData = response.data.form.data;
        setFormData(prev => mergeSaved(prev, savedData));
        // Load signature if exists
        if (savedData.clientSignature && signatureRef.current) {
          signatureRef.current.fromDataURL(savedData.clientSignature);
        }
      }
    } catch (error) {
      // Form doesn't exist yet, that's okay
      console.log('No existing form found, will create new');
    }
  };

  const fetchPatientData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/patients/${patientId}`);
      if (response.data) {
        const patient = response.data.patient || response.data;
        setFormData(prev => fillEmpty(prev, {
          
          clientName: `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
          address: [patient.address, patient.city, [patient.state, patient.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', '),
          dateOfBirth: patient.dateOfBirth || '',
          phoneNumber: patient.phone || '',
          sex: patient.gender || '',
          religion: patient.religion || '',
          doctorName: patient.physicianName || patient.primaryPhysician || '',
          doctorPhone: patient.physicianPhone || patient.primaryPhysicianPhone || '',
          allergies: patient.allergies || '',
          emergencyContactName: patient.emergencyContactName || '',
          emergencyContactPhone: patient.emergencyContactPhone || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching patient:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleClearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Get signature data
      let signatureData = formData.clientSignature;
      if (signatureRef.current && !signatureRef.current.isEmpty()) {
        signatureData = signatureRef.current.toDataURL();
      }

      const payload = {
        ...formData,
        clientSignature: signatureData,
        patientId,
        formType: 'emergency_info',
      };

      const response = await api.post('/admission-forms/save', payload);
      if (response.data.patientUpdated) {
        toast.success('Form saved & patient record updated!', {
          description: 'Emergency info synced to Patient Demographics, 485, and Nursing docs.'
        });
      } else {
        toast.success('Emergency Information form saved successfully');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    exportFormPdfFromDom(document.getElementById('main-content'), 'Emergency Information', patientId, 'Emergency_Info.pdf');
  };

  if (!patientId) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
              <p className="text-yellow-800">Please select a patient to complete this form.</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="emergency-info-form">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-t-xl p-6 text-white print:bg-red-500">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">ATTENTION - Emergency Information</h1>
                <p className="text-red-100">IVORY HOME HEALTH CARE, INC.</p>
              </div>
            </div>
            <div className="flex gap-2 print:hidden">
              <button
                data-testid="print-form-btn"
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-white text-red-600 hover:bg-red-50 rounded-lg transition-colors font-medium"
                data-testid="save-form-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Form'}
              </button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-b-xl shadow-lg p-8 space-y-8">
          {/* Notice */}
          <div className="bg-red-50 border-l-4 border-red-500 p-4">
            <p className="text-red-800 font-medium">
              POST THIS INFORMATION IN AN ACCESSIBLE LOCATION (e.g., on your refrigerator)
            </p>
          </div>

          {/* Client Information */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-red-500" />
              Client Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                <input
                  type="text"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  data-testid="client-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  data-testid="dob-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  data-testid="address-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  data-testid="phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sex</label>
                <select
                  name="sex"
                  value={formData.sex}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">Select...</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Religion</label>
                <input
                  type="text"
                  name="religion"
                  value={formData.religion}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Birth Place</label>
                <input
                  type="text"
                  name="birthPlace"
                  value={formData.birthPlace}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hair Color</label>
                <input
                  type="text"
                  name="hairColor"
                  value={formData.hairColor}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Eye Color</label>
                <input
                  type="text"
                  name="eyeColor"
                  value={formData.eyeColor}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Any Distinguishing Features</label>
                <textarea
                  name="distinguishingFeatures"
                  value={formData.distinguishingFeatures}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
          </section>

          {/* Doctor Information */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              Doctor Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Doctor's Name</label>
                <input
                  type="text"
                  name="doctorName"
                  value={formData.doctorName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  data-testid="doctor-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Doctor's Phone Number</label>
                <input
                  type="tel"
                  name="doctorPhone"
                  value={formData.doctorPhone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  data-testid="doctor-phone-input"
                />
              </div>
            </div>
          </section>

          {/* Medical Information */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Pill className="w-5 h-5 text-red-500" />
              Medical Information
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Special Medical Conditions</label>
                <textarea
                  name="specialMedicalConditions"
                  value={formData.specialMedicalConditions}
                  onChange={handleChange}
                  rows={3}
                  placeholder="List any special medical conditions that emergency responders should know about..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  data-testid="medical-conditions-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Allergies</label>
                <textarea
                  name="allergies"
                  value={formData.allergies}
                  onChange={handleChange}
                  rows={2}
                  placeholder="List all known allergies (medications, foods, environmental)..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  data-testid="allergies-input"
                />
              </div>
            </div>
          </section>

          {/* Emergency Contact */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-red-500" />
              In Case of Emergency Contact
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                <input
                  type="text"
                  name="emergencyContactName"
                  value={formData.emergencyContactName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  data-testid="emergency-contact-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                <input
                  type="tel"
                  name="emergencyContactPhone"
                  value={formData.emergencyContactPhone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  data-testid="emergency-contact-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                <input
                  type="text"
                  name="emergencyContactRelationship"
                  value={formData.emergencyContactRelationship}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
          </section>

          {/* Additional Information */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pets (type, name, location)</label>
                <input
                  type="text"
                  name="pets"
                  value={formData.pets}
                  onChange={handleChange}
                  placeholder="e.g., Dog named Max, usually in backyard"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Any Additional Information</label>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Any other information that may be helpful in an emergency..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
          </section>

          {/* Agency Contact */}
          <section className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Agency Contact Information</h3>
            <div className="text-sm text-gray-700 space-y-1">
              <p><strong>IVORY HOME HEALTH CARE, INC.</strong></p>
              <p>Tel: {AGENCY.phone} · Fax: {AGENCY.fax}</p>
              <p>Address: {AGENCY.address}, {AGENCY.cityStateZip}</p>
              <p>24-Hour Emergency Line: {AGENCY.phone}</p>
            </div>
          </section>

          {/* Signature */}
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Signature</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Signature</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: 'w-full h-32 bg-white',
                      style: { width: '100%', height: '128px' }
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleClearSignature}
                  className="mt-2 text-sm text-red-600 hover:text-red-700"
                >
                  Clear Signature
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  name="signatureDate"
                  value={formData.signatureDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            </div>
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EmergencyInfoForm;
